import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Star, ArrowRight, Locate } from "lucide-react";
import { useState, useEffect, useDeferredValue } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Stats {
  businessCount: number;
  practitionerCount: number;
  customerCount: number;
  bookingCount: number;
}

export function HeroSection() {
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");
  const [stats, setStats] = useState<Stats>({
    businessCount: 0,
    practitionerCount: 0,
    customerCount: 0,
    bookingCount: 0
  });
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const navigate = useNavigate();
  
  const deferredStats = useDeferredValue(stats);
  
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStats();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, []);
  
  const fetchStats = async () => {
    try {
      const [businessRes, practitionerRes, customerRes, bookingRes] = await Promise.all([
        supabase.from("businesses").select("*", { count: "exact", head: true }),
        supabase.from("practitioners_public").select("*", { count: "exact", head: true }),
        supabase.from("profiles").select("*", { count: "exact", head: true }).eq("role", "customer"),
        supabase.from("bookings").select("*", { count: "exact", head: true }).eq("status", "completed")
      ]);
      
      setStats({
        businessCount: businessRes.count || 0,
        practitionerCount: practitionerRes.count || 0,
        customerCount: customerRes.count || 0,
        bookingCount: bookingRes.count || 0
      });
    } catch (error) {
      // Silently fail - stats are non-critical
    } finally {
      setIsStatsLoading(false);
    }
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(searchQuery)}&location=${encodeURIComponent(location)}`);
  };
  
  const handleUseLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => setLocation("Near me"),
        () => setLocation("")
      );
    }
  };

  const hasStats = deferredStats.businessCount > 0 || deferredStats.practitionerCount > 0 || 
                   deferredStats.customerCount > 0 || deferredStats.bookingCount > 0;
  
  return (
    <section className="relative min-h-[100svh] md:min-h-[90vh] flex items-center gradient-hero overflow-hidden">
      <div className="absolute inset-0 overflow-hidden hidden md:block" aria-hidden="true">
        <div className="absolute top-20 left-10 w-72 h-72 bg-wellness-teal/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-wellness-warm/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "-3s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-wellness-sage/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 pt-20 pb-8 md:pt-24 md:pb-16 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-primary/10 border border-primary/20 mb-6 md:mb-8 animate-fade-up">
            <Star className="w-3.5 h-3.5 md:w-4 md:h-4 text-wellness-warm" aria-hidden="true" />
            <span className="text-xs md:text-sm font-medium text-foreground">
              Your Global Wellness Booking Platform
            </span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-4 md:mb-6 animate-fade-up leading-tight" style={{ animationDelay: "0.1s" }}>
            Book Massage & Wellness{" "}
            <span className="text-gradient">Services</span>
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            Worldwide
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 md:mb-10 animate-fade-up px-2" style={{ animationDelay: "0.2s" }}>
            Find top-rated spas, massage therapists, beauty salons, barber shops, fitness trainers, and holistic wellness practitioners near you. Book wellness services online instantly.
          </p>

          <form onSubmit={handleSearch} className="bg-card rounded-2xl md:rounded-3xl shadow-wellness-lg p-3 md:p-2 max-w-3xl mx-auto animate-fade-up" style={{ animationDelay: "0.3s" }} role="search" aria-label="Search wellness services">
            <div className="flex flex-col gap-3 md:flex-row md:gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" aria-hidden="true" />
                <input 
                  type="text" 
                  placeholder="Search services or spas..." 
                  value={searchQuery} 
                  onChange={e => setSearchQuery(e.target.value)} 
                  className="w-full h-14 md:h-14 pl-12 pr-4 rounded-xl bg-secondary/50 border-0 text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  aria-label="Search for wellness services or spas"
                />
              </div>
              
              <div className="flex-1 relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" aria-hidden="true" />
                <input 
                  type="text" 
                  placeholder="Location" 
                  value={location} 
                  onChange={e => setLocation(e.target.value)} 
                  className="w-full h-14 md:h-14 pl-12 pr-14 rounded-xl bg-secondary/50 border-0 text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  aria-label="Enter location"
                />
                <button 
                  type="button" 
                  onClick={handleUseLocation} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 focus:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary transition-colors touch-manipulation" 
                  aria-label="Use my current location"
                >
                  <Locate className="w-5 h-5 text-primary" aria-hidden="true" />
                </button>
              </div>
              
              <Button type="submit" variant="hero" size="lg" className="h-14 px-8 text-base font-semibold touch-manipulation active:scale-[0.98] transition-transform">
                Search
                <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </Button>
            </div>
          </form>

          <nav className="flex flex-wrap justify-center gap-2 mt-4 md:hidden animate-fade-up" style={{ animationDelay: "0.35s" }} aria-label="Quick search categories">
            <Link to="/browse?category=spa_wellness" className="px-4 py-2 min-h-[44px] rounded-full bg-primary/10 text-primary text-sm font-medium touch-manipulation active:bg-primary/20 flex items-center">
              Spas near me
            </Link>
            <Link to="/browse?category=massage_therapy" className="px-4 py-2 min-h-[44px] rounded-full bg-primary/10 text-primary text-sm font-medium touch-manipulation active:bg-primary/20 flex items-center">
              Massage
            </Link>
            <Link to="/browse?category=yoga_meditation" className="px-4 py-2 min-h-[44px] rounded-full bg-primary/10 text-primary text-sm font-medium touch-manipulation active:bg-primary/20 flex items-center">
              Yoga
            </Link>
          </nav>

          {!isStatsLoading && hasStats && (
            <div className="grid grid-cols-2 md:flex md:flex-wrap md:justify-center gap-4 md:gap-8 lg:gap-16 mt-8 md:mt-12 animate-fade-up" style={{ animationDelay: "0.4s" }}>
              {deferredStats.businessCount > 0 && (
                <div className="text-center">
                  <p className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">{deferredStats.businessCount}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">Wellness Centers</p>
                </div>
              )}
              {deferredStats.practitionerCount > 0 && (
                <div className="text-center">
                  <p className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">{deferredStats.practitionerCount}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">Practitioners</p>
                </div>
              )}
              {deferredStats.customerCount > 0 && (
                <div className="text-center">
                  <p className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">{deferredStats.customerCount}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">Happy Customers</p>
                </div>
              )}
              {deferredStats.bookingCount > 0 && (
                <div className="text-center">
                  <p className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">{deferredStats.bookingCount}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">Bookings Completed</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-12 md:h-auto">
          <path d="M0 120L60 110C120 100 240 80 360 75C480 70 600 80 720 85C840 90 960 90 1080 85C1200 80 1320 70 1380 65L1440 60V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
}
